// useBanner.ts
import { useState, useEffect, useRef, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";

interface BaseBannerItem {
  id?: number | string;
  name: string;
  description?: string;
  files: string[];
  discount?: number;
  showText?: boolean;
}

interface UseBannerProps<T extends BaseBannerItem> {
  items: T[];
  variant?: "base" | "split" | "expanded" | "compact" | "hero";
}

const AUTOPLAY_INTERVAL_IMAGE = 5000;
const AUTOPLAY_INTERVAL_VIDEO = 15000;
const RESUME_DELAY = 30000;

export function useBanner<T extends BaseBannerItem>({
  items,
  variant = "base",
}: UseBannerProps<T>) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<
    Record<string | number, boolean>
  >({});
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const resumeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isPoster = useCallback((file: string) => file?.includes("poster_"), []);
  const isVideo = useCallback((file: string) => file?.includes("video_"), []);
  const isImage = useCallback(
    (file: string) => !isVideo(file) && !isPoster(file),
    [isVideo, isPoster],
  );

  const getCurrentMediaType = useCallback(() => {
    const currentItem = items[selectedIndex];
    if (!currentItem?.files) return "image";
    const mediaFile = currentItem.files.find((f: string) => !isPoster(f)) || "";
    return isVideo(mediaFile) ? "video" : "image";
  }, [items, selectedIndex, isPoster, isVideo]);

  // Приоритет: сначала видео, потом картинка
  const getMediaFile = useCallback(
    (files: string[]) => {
      // Сначала ищем видео
      const videoFile = files?.find((f: string) => isVideo(f));
      if (videoFile) return videoFile;
      // Если видео нет — ищем картинку (не постер)
      return files?.find((f: string) => !isPoster(f)) || "";
    },
    [isVideo, isPoster],
  );

  const getOverlayImage = useCallback(
    (files: string[]) => {
      return files?.find((f: string) => isImage(f)) || "";
    },
    [isImage],
  );

  const hasVideoAndImage = useCallback(
    (files: string[]) => {
      const hasVideo = files?.some((f: string) => isVideo(f));
      const hasImage = files?.some((f: string) => isImage(f));
      return hasVideo && hasImage;
    },
    [isVideo, isImage],
  );

  useEffect(() => {
    if (!emblaApi || items.length <= 1) return;

    const clearTimer = () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
        autoplayTimerRef.current = null;
      }
    };

    const startAutoplay = () => {
      clearTimer();
      const interval = getCurrentMediaType() === "video"
        ? AUTOPLAY_INTERVAL_VIDEO
        : AUTOPLAY_INTERVAL_IMAGE;

      autoplayTimerRef.current = setInterval(() => {
        if (!isUserInteracting && emblaApi.canScrollNext()) {
          emblaApi.scrollNext();
        }
      }, interval);
    };

    startAutoplay();

    return () => {
      clearTimer();
    };
  }, [emblaApi, items.length, isUserInteracting, getCurrentMediaType]);

  useEffect(() => {
    if (isUserInteracting) {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = setTimeout(() => {
        setIsUserInteracting(false);
      }, RESUME_DELAY);
    }
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, [isUserInteracting]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    const onPointerDown = () => {
      setIsUserInteracting(true);
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", onPointerDown);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", onPointerDown);
    };
  }, [emblaApi]);

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([id, video]) => {
      if (!video) return;
      const isActive = Number(id) === selectedIndex;

      if (isActive) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            const onUserInteraction = () => {
              video.play().catch(() => {});
              document.removeEventListener("click", onUserInteraction);
            };
            const slide = video.closest(".slide");
            if (slide) {
              slide.addEventListener("click", onUserInteraction, { once: true });
            }
          });
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [selectedIndex]);

  const getVariantClass = useCallback(() => {
    switch (variant) {
      case "expanded":
        return "expanded";
      case "split":
        return "split";
      case "compact":
        return "compact";
      case "hero":
        return "hero";
      default:
        return "base";
    }
  }, [variant]);

  const handleImageLoad = useCallback((itemId: string | number | undefined) => {
    if (itemId) {
      setLoadedImages((prev) => ({ ...prev, [itemId]: true }));
    }
  }, []);

  const handlePrev = useCallback(() => {
    setIsUserInteracting(true);
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    setIsUserInteracting(true);
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const handleDotClick = useCallback(
    (index: number) => {
      setIsUserInteracting(true);
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const isImageLoaded = useCallback(
    (itemId?: string | number) => {
      return itemId ? loadedImages[itemId] : false;
    },
    [loadedImages],
  );

  const registerVideoRef = useCallback(
    (index: number, el: HTMLVideoElement | null) => {
      if (el) {
        videoRefs.current[String(index)] = el;
      } else {
        delete videoRefs.current[String(index)];
      }
    },
    [],
  );

  return {
    emblaRef,
    selectedIndex,
    getVariantClass,
    handleImageLoad,
    handlePrev,
    handleNext,
    handleDotClick,
    isImageLoaded,
    hasCarousel: items.length > 1,
    registerVideoRef,
    isVideo,
    isPoster,
    isImage,
    getMediaFile,
    getOverlayImage,
    hasVideoAndImage,
  };
}